import type { CrudFilter, CrudSort } from '@refinedev/core';
import { useParsed } from '@refinedev/core';
import { useEffect, useRef, useState } from 'react';

interface UseListStatePersistenceParams {
  resource: string;
  filters: CrudFilter[];
  setFilters: (filters: CrudFilter[], behavior?: 'merge' | 'replace') => void;
  sorters: CrudSort[];
  setSorters: (sorters: CrudSort[]) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  onReady?: () => void;
  /**
   * Filtros que deben estar siempre presentes al restaurar.
   * Si el storage no los contiene, se agregan con los valores por defecto indicados.
   */
  defaultFilters?: CrudFilter[];
}

/**
 * Combina filtros restaurados con los defaults: los defaults solo aplican
 * cuando el campo correspondiente no está presente en los filtros restaurados.
 */
const mergeWithDefaultFilters = (stored: CrudFilter[], defaults: CrudFilter[]): CrudFilter[] => {
  const result = [...stored];

  for (const def of defaults) {
    const alreadyPresent = stored.some(
      f => 'field' in f && 'field' in def && f.field === (def as { field: string }).field,
    );

    if (!alreadyPresent) result.push(def);
  }

  return result;
};

export const useListStatePersistence = ({
  resource,
  filters,
  setFilters,
  sorters,
  setSorters,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  onReady,
  defaultFilters,
}: UseListStatePersistenceParams) => {
  // Se usa para leer el estado de la URL solo dentro del listado.
  const { params } = useParsed();
  // Flags para evitar que el primer render sobrescriba storage.
  const didRestoreFiltersRef = useRef(false);
  const didRestorePaginationRef = useRef(false);
  const didRestoreSortersRef = useRef(false);
  const didRestoreReadyRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const storedFiltersRef = useRef<CrudFilter[] | null>(null);
  // Helpers de storage (sessionStorage por pestaña).
  const readFromStorage = <T,>(key: string): T | null => {
    try {
      const stored = sessionStorage.getItem(key);

      if (!stored) {
        return null;
      }

      return JSON.parse(stored) as T;
    } catch {
      return null;
    }
  };

  const writeToStorage = (key: string, value: unknown) => {
    try {
      if (value === null) {
        sessionStorage.removeItem(key);

        return;
      }

      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore storage errors
    }
  };

  const getListFilters = () => {
    // Lee filtros persistidos por recurso.
    const parsed = readFromStorage<CrudFilter[]>(`listFilters:${resource}`);

    return Array.isArray(parsed) ? parsed : null;
  };

  const setListFilters = (nextFilters: CrudFilter[]) => {
    // Guarda filtros, o limpia si están vacíos.
    if (!nextFilters || nextFilters.length === 0) {
      writeToStorage(`listFilters:${resource}`, null);

      return;
    }

    writeToStorage(`listFilters:${resource}`, nextFilters);
  };

  const getListPagination = () => {
    // Lee paginado persistido por recurso.
    const parsed = readFromStorage<{ currentPage: number; pageSize: number }>(
      `listPagination:${resource}`,
    );

    if (!parsed || typeof parsed.currentPage !== 'number' || typeof parsed.pageSize !== 'number') {
      return null;
    }

    return parsed;
  };

  const setListPagination = (pagination: { currentPage: number; pageSize: number }) => {
    // Guarda paginado, o limpia si es inválido.
    if (!pagination || pagination.currentPage < 1 || pagination.pageSize < 1) {
      writeToStorage(`listPagination:${resource}`, null);

      return;
    }

    writeToStorage(`listPagination:${resource}`, pagination);
  };

  const getListSorters = () => {
    // Lee sorters persistidos por recurso.
    const parsed = readFromStorage<CrudSort[]>(`listSorters:${resource}`);

    return Array.isArray(parsed) ? parsed : null;
  };

  const setListSorters = (nextSorters: CrudSort[]) => {
    // Guarda sorters, o limpia si están vacíos.
    if (!nextSorters || nextSorters.length === 0) {
      writeToStorage(`listSorters:${resource}`, null);

      return;
    }

    writeToStorage(`listSorters:${resource}`, nextSorters);
  };

  // Detecta si la URL ya trae filtros/paginado/sorters.
  const hasUrlFilters = Array.isArray(params?.filters) && params.filters.length > 0;
  const parsedCurrentPage =
    typeof params?.currentPage === 'number'
      ? params.currentPage
      : params?.currentPage
        ? Number(params.currentPage)
        : undefined;
  const parsedPageSize =
    typeof params?.pageSize === 'number'
      ? params.pageSize
      : params?.pageSize
        ? Number(params.pageSize)
        : undefined;
  const hasUrlPagination = Number.isFinite(parsedCurrentPage) || Number.isFinite(parsedPageSize);
  const hasUrlSorters = Array.isArray(params?.sorters) && params.sorters.length > 0;

  useEffect(() => {
    // Restaurar filtros desde storage si la URL no trae filtros.
    if (didRestoreFiltersRef.current) {
      return;
    }

    if (hasUrlFilters) {
      didRestoreFiltersRef.current = true;

      return;
    }

    const storedFilters = getListFilters();
    // Aplica defaults sobre los filtros restaurados para garantizar campos obligatorios.
    const filtersToApply = defaultFilters?.length
      ? mergeWithDefaultFilters(storedFilters ?? [], defaultFilters)
      : (storedFilters ?? []);

    storedFiltersRef.current = filtersToApply.length ? filtersToApply : null;
    if (filtersToApply.length) {
      setFilters(filtersToApply, 'replace');
    }

    didRestoreFiltersRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUrlFilters, resource, setFilters]);

  useEffect(() => {
    // Restaurar sorters desde storage si la URL no trae sorters.
    if (didRestoreSortersRef.current) {
      return;
    }

    if (hasUrlSorters) {
      didRestoreSortersRef.current = true;

      return;
    }

    const storedSorters = getListSorters();

    if (storedSorters?.length) {
      setSorters(storedSorters);
    }

    didRestoreSortersRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUrlSorters, resource, setSorters]);

  useEffect(() => {
    // Restaurar paginado desde storage si la URL no trae paginado.
    if (didRestorePaginationRef.current) {
      return;
    }

    if (hasUrlPagination) {
      didRestorePaginationRef.current = true;

      return;
    }

    const storedPagination = getListPagination();

    if (storedPagination) {
      setPageSize(storedPagination.pageSize);
      setCurrentPage(storedPagination.currentPage);
    }

    didRestorePaginationRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUrlPagination, resource, setCurrentPage, setPageSize]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Marca el hook como listo cuando terminan las restauraciones.
    if (didRestoreReadyRef.current) {
      return;
    }

    if (
      didRestoreFiltersRef.current &&
      didRestoreSortersRef.current &&
      didRestorePaginationRef.current
    ) {
      didRestoreReadyRef.current = true;
      setIsReady(true);
      onReady?.();
    }
  });

  useEffect(() => {
    // Persistir filtros cuando el listado ya terminó de restaurar.
    if (!didRestoreFiltersRef.current) {
      return;
    }

    // Evita borrar storage con un estado vacío inicial.
    if (!hasUrlFilters && storedFiltersRef.current?.length && filters.length === 0) {
      return;
    }

    setListFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, hasUrlFilters, resource]);

  useEffect(() => {
    // Persistir sorters cuando el listado ya terminó de restaurar.
    if (!didRestoreSortersRef.current) {
      return;
    }

    setListSorters(sorters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, sorters]);

  useEffect(() => {
    // Persistir paginado cuando el listado ya terminó de restaurar.
    if (!didRestorePaginationRef.current) {
      return;
    }

    setListPagination({ currentPage, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, resource]);

  return { isReady };
};
